import React, { useState } from 'react';
import { toast } from 'sonner';
import { Link2, Plus, Trash2, AlertCircle, Building2, CheckCircle2, Loader2, ArrowRight } from 'lucide-react';
import { EmptyState } from './_shared';
import { AddReceiptsToSheetModal } from './AddReceiptsToSheetModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ===================================================================
export function EditSheetModal({ token, sheet, onClose, onSuccess }) {
  const [notes, setNotes] = useState(sheet.notes || '');
  const [items, setItems] = useState(() =>
    (sheet.items || []).map((it) => ({
      id: it.id,
      _isNew: false,
      supplier_name: it.supplier_name,
      receipt_number: it.receipt_number,
      receipt_date: it.receipt_date,
      project_number: it.project_number,
      invoice_number: it.invoice_number || '',
      invoice_date: it.invoice_date || '',
      amount: it.amount != null ? String(it.amount) : '',
      group_id: it.group_id || null,
      // مخزون لحقول الـ "إضافة" — تُرسل للـ backend في add_items
      _new_payload: null,
    }))
  );
  const [removedIds, setRemovedIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);

  const updateItem = (id, field, value) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, [field]: value } : it)));
  };

  const removeRow = (id) => {
    const target = items.find(it => it.id === id);
    if (!target) return;
    if (target._isNew) {
      // إزالة محلية بدون استدعاء backend
      setItems((prev) => prev.filter((it) => it.id !== id));
      return;
    }
    if (!window.confirm('فك ربط هذا الاستلام من الكشف؟ سيُحذف من الكشف وتُلغى الفاتورة المرتبطة به.')) return;
    setItems((prev) => prev.filter((it) => it.id !== id));
    setRemovedIds((prev) => [...prev, id]);
  };

  const handleAddReceipts = (newItems) => {
    // newItems: [{ delivery_id, supplier_index, receipt_index, supplier_name, receipt (obj), invoice_number, invoice_date, amount, group_id }]
    const mapped = newItems.map((n, i) => {
      const r = n.receipt || {};
      const tempId = `new-${Date.now()}-${i}`;
      return {
        id: tempId,
        _isNew: true,
        supplier_name: n.supplier_name,
        receipt_number: r.receipt_number,
        receipt_date: r.receipt_date,
        project_number: r.project_number,
        invoice_number: n.invoice_number || '',
        invoice_date: n.invoice_date || '',
        amount: n.amount != null ? String(n.amount) : '',
        group_id: n.group_id || null,
        _new_payload: {
          receipt_delivery_id: n.delivery_id,
          supplier_index: n.supplier_index,
          receipt_index: n.receipt_index,
          supplier_name: n.supplier_name,
          receipt_number: r.receipt_number,
          receipt_date: r.receipt_date,
          project_number: r.project_number,
          project_name: r.project_name,
        },
      };
    });
    setItems((prev) => [...prev, ...mapped]);
    setAddModalOpen(false);
    toast.success(`أُضيف ${mapped.length} استلام${mapped.length > 1 ? 'ات' : ''} — اضغط "حفظ التعديلات" للتأكيد`);
  };

  const handleSave = async () => {
    const validItems = items.filter((it) => it.invoice_number.trim());
    if (validItems.length === 0 && items.length === 0 && removedIds.length === 0) {
      toast.error('لا توجد تغييرات للحفظ');
      return;
    }
    if (validItems.length !== items.length) {
      toast.error('يجب أن يحتوي كل سطر متبقٍ على رقم فاتورة. لحذف سطر استخدم زر السلة.');
      return;
    }
    setSaving(true);
    try {
      const updateItems = validItems
        .filter(it => !it._isNew)
        .map((it) => ({
          id: it.id,
          invoice_number: it.invoice_number.trim(),
          invoice_date: it.invoice_date || null,
          amount: it.amount !== '' ? parseFloat(it.amount) : null,
        }));
      const addItems = validItems
        .filter(it => it._isNew)
        .map((it) => ({
          ...it._new_payload,
          invoice_number: it.invoice_number.trim(),
          invoice_date: it.invoice_date || null,
          amount: it.amount !== '' ? parseFloat(it.amount) : null,
          group_id: it.group_id || null,
        }));
      const payload = {
        notes,
        items: updateItems,
        remove_item_ids: removedIds,
        add_items: addItems,
      };
      const res = await fetch(`${API_URL}/api/invoice-matching/sheets/${sheet.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const parts = [];
        if (addItems.length) parts.push(`أُضيف ${addItems.length}`);
        if (removedIds.length) parts.push(`فُكَّ ${removedIds.length}`);
        const suffix = parts.length ? ` (${parts.join(' • ')})` : '';
        toast.success(`تم تحديث كشف #${sheet.sheet_number}${suffix}`);
        onSuccess();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'فشل في الحفظ');
      }
    } catch {
      toast.error('فشل في الحفظ');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-stretch sm:items-center sm:justify-center sm:p-4" dir="rtl">
      <div className="bg-gray-50 w-full sm:max-w-4xl sm:rounded-2xl flex flex-col h-full sm:h-[92vh] overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b sticky top-0 z-10 px-4 py-3 flex items-center gap-3">
          <button onClick={onClose} className="w-10 h-10 -mr-2 rounded-full hover:bg-gray-100 flex items-center justify-center" data-testid="edit-close-btn">
            <ArrowRight className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-gray-900 text-base sm:text-lg">تعديل كشف #{sheet.sheet_number}</h2>
            <p className="text-xs text-gray-500">عدّل، احذف، أو أضف استلامات + الملاحظات</p>
          </div>
          <button
            onClick={() => setAddModalOpen(true)}
            data-testid="edit-add-receipts-btn"
            className="hidden sm:flex h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold items-center gap-1.5"
          >
            <Plus className="w-4 h-4" /> إضافة استلامات
          </button>
          <span className="bg-orange-100 text-orange-700 text-xs font-semibold px-2.5 py-1 rounded-full">{items.length} فاتورة</span>
        </header>

        {/* Mobile add button */}
        <div className="sm:hidden bg-white border-b px-4 py-2">
          <button
            onClick={() => setAddModalOpen(true)}
            data-testid="edit-add-receipts-mobile-btn"
            className="w-full h-10 rounded-lg bg-emerald-600 active:bg-emerald-700 text-white text-sm font-bold flex items-center justify-center gap-1.5"
          >
            <Plus className="w-4 h-4" /> إضافة استلامات للكشف
          </button>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3">
          {items.length === 0 ? (
            <EmptyState icon={AlertCircle} title="لا توجد بنود" subtitle="تم فك ربط كل الاستلامات. اضغط حفظ لإتمام العملية أو ألغِ التعديل." />
          ) : items.map((it) => (
            <div key={it.id} className={`border-2 rounded-2xl p-3 ${it._isNew ? 'border-emerald-300 bg-emerald-50/40' : 'border-gray-200 bg-white'}`}>
              <div className="flex items-start gap-2 mb-3">
                <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center shrink-0">
                  <Building2 className="w-4 h-4 text-emerald-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-gray-900 truncate flex items-center gap-2">
                    {it.supplier_name}
                    {it._isNew && <span className="bg-emerald-600 text-white text-[10px] px-1.5 py-0.5 rounded">جديد</span>}
                  </div>
                  <div className="text-xs text-gray-500 flex flex-wrap gap-x-2">
                    <span>استلام #{it.receipt_number}</span>
                    {it.receipt_date && <><span>•</span><span>{it.receipt_date}</span></>}
                    {it.project_number && <><span>•</span><span>مشروع {it.project_number}</span></>}
                  </div>
                  {it.group_id && (
                    <div className="text-[10px] text-purple-700 mt-0.5 flex items-center gap-1">
                      <Link2 className="w-3 h-3" /> مجموعة فاتورة موحَّدة
                    </div>
                  )}
                </div>
                <button
                  onClick={() => removeRow(it.id)}
                  data-testid={`edit-remove-row-${it.id}`}
                  className="w-9 h-9 rounded-lg bg-red-50 hover:bg-red-100 active:bg-red-200 text-red-600 flex items-center justify-center shrink-0"
                  title="فك ربط هذا الاستلام"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <input
                  type="text"
                  placeholder="رقم الفاتورة *"
                  value={it.invoice_number}
                  onChange={(e) => updateItem(it.id, 'invoice_number', e.target.value)}
                  data-testid={`edit-invoice-num-${it.id}`}
                  className="h-11 px-3 border-2 border-gray-200 rounded-lg text-sm font-semibold focus:border-orange-500 outline-none"
                />
                <input
                  type="date"
                  value={it.invoice_date}
                  onChange={(e) => updateItem(it.id, 'invoice_date', e.target.value)}
                  className="h-11 px-3 border-2 border-gray-200 rounded-lg text-sm focus:border-orange-500 outline-none"
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="المبلغ"
                  value={it.amount}
                  onChange={(e) => updateItem(it.id, 'amount', e.target.value)}
                  className="h-11 px-3 border-2 border-gray-200 rounded-lg text-sm focus:border-orange-500 outline-none"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <footer className="border-t bg-white px-4 py-3 flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="ملاحظات الكشف"
            className="flex-1 h-12 px-3 border-2 border-gray-200 rounded-xl text-sm focus:border-orange-500 outline-none"
            data-testid="edit-notes-input"
          />
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="edit-save-btn"
            className="min-h-[52px] bg-orange-600 hover:bg-orange-700 active:bg-orange-800 text-white font-bold rounded-xl px-6 disabled:opacity-50 flex items-center justify-center gap-2 text-base"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            حفظ التعديلات
          </button>
        </footer>
      </div>

      {addModalOpen && (
        <AddReceiptsToSheetModal
          token={token}
          existingReceiptKeys={new Set(items.map(it => {
            // مفتاح فريد لكل استلام: delivery_id-supplier_index-receipt_index
            const dp = it._new_payload || {};
            const did = dp.receipt_delivery_id;
            if (did != null) return `${did}-${dp.supplier_index}-${dp.receipt_index}`;
            // للبنود الموجودة: لا نملك delivery_id محلياً، نستثني
            return null;
          }).filter(Boolean))}
          onClose={() => setAddModalOpen(false)}
          onApply={handleAddReceipts}
        />
      )}
    </div>
  );
}

// ===================================================================
// 7) Link Same Invoice Modal — إدخال فاتورة واحدة لعدة استلامات
